import { PRODUCT_TYPES } from "@shared/constants/paidFeaturesHierarchy";
import AiIconFilled from "@shared/svg/ai-active.svg?react";
import AiIcon from "@shared/svg/ai-grey.svg?react";
import SidebarAnalyticsFilledIconV2 from "@shared/svg/analytics-active.svg?react";
import SidebarAnalyticsIconV2 from "@shared/svg/analytics.svg?react";
import AssetHubIconActive from "@shared/svg/asset-hub-active.svg?react";
import AssetHubIcon from "@shared/svg/asset-hub.svg?react";
import AssetsManagementActive from "@shared/svg/assets-management-active.svg?react";
import AssetsManagement from "@shared/svg/assets-management.svg?react";
import CalendarBlankFilled from "@shared/svg/calendar-blank-filled.svg?react";
import CalendarBlank from "@shared/svg/calendar-blank.svg?react";
import CustomerPortalActive from "@shared/svg/customer-portal-active.svg?react";
import CustomerPortal from "@shared/svg/customer-portal.svg?react";
import SidebarFacilityFilledIconV2 from "@shared/svg/facilities-active.svg?react";
import SidebarFacilityIconV2 from "@shared/svg/facilities.svg?react";
import FieldServiceIconActive from "@shared/svg/field-service-active.svg?react";
import FieldServiceIcon from "@shared/svg/field-service.svg?react";
import IndustrialAiIconActive from "@shared/svg/industrial-ai-active.svg?react";
import IndustrialAiIcon from "@shared/svg/industrial-ai.svg?react";
import * as React from "react";
import { Navigate, useParams } from "react-router-dom";

import APP_FEATURES from "$/settings/app-features.json";
import PAID_FEATURES from "$/settings/paid-features.json";
import { COLOR } from "~/constants";
import useNomenclature from "~/hooks/useNomenclature";
import {
  EnvelopFilled,
  EnvelopOutline,
  InventoryFilledIcon,
  InventoryIcon,
  SidebarAnalyticsFilledIcon,
  SidebarAnalyticsIcon,
  SidebarFacilityFilledIcon,
  SidebarFacilityIcon,
  SidebarMyTicketFilledIcon,
  SidebarMyTicketfolderIcon,
  SidebarProceduresFilledIcon,
  SidebarProceduresIcon,
  HourglassIcon,
} from "~/icons/icons";
import { getEnums } from "~/utils";
import { useIsProductCatalogEnabled } from "~/utils/appFeatures";
import isPaidFeatureAvailable from "~/utils/isPaidFeatureAvailable";

const paidFeatures = getEnums(PAID_FEATURES, "reference");
const appFeatures = getEnums(APP_FEATURES, "reference");

const MyWorkOrdersPage = React.lazy(() =>
  import("~/containers/ticket/MyWorkOrdersPage"),
);
const WorkOrdersPage = React.lazy(() =>
  import("~/containers/ticket/WorkOrdersPage"),
);
const ArchivedWorkOrdersPage = React.lazy(() =>
  import("~/containers/ticket/ArchivedWorkOrdersPage"),
);
const SupportTickets = React.lazy(() =>
  import("~/pages/SupportTickets/SupportTickets"),
);
const SupportTicketViewPage = React.lazy(() =>
  import("~/pages/SupportTicketView/SupportTicketView"),
);
const Assets = React.lazy(() => import("~/pages/Assets/Assets"));
const CustomerPortalLayout = React.lazy(() =>
  import("customer-portal/workspace"),
);
const WorkspaceLayout = React.lazy(() =>
  import("#/src/layout/WorkspaceLayout"),
);
const Connections = React.lazy(() =>
  import("~/containers/Connections/AllConnections"),
);
const AllContacts = React.lazy(() => import("~/pages/Contacts"));
const SingleConnection = React.lazy(() => import("~/pages/SingleConnection"));
const AssetTemplates = React.lazy(() =>
  import("~/containers/Assets/AssetTemplates"),
);
const WorkOrderDetailsPage = React.lazy(() =>
  import("~/features/WorkOrderDetails").then((m) => ({
    default: m.WorkOrderDetails,
  })),
);
const AssetView = React.lazy(() => import("~/pages/AssetView/AssetView"));
const AssetTemplateSingle = React.lazy(() =>
  import("~/containers/AssetTemplateSingle"),
);
const MyPortalsFutureVision = React.lazy(() =>
  import("~/pages/Portals/Portals"),
);
const SinglePortal = React.lazy(() =>
  import("#/src/pages/SinglePortal/SinglePortal"),
);

const SharedProducts = React.lazy(() =>
  import("~/containers/CustomerPortal/SharedProducts"),
);
const ConnectionRequests = React.lazy(() =>
  import("~/containers/Assets/ConnectionRequests"),
);

const MainSettingsLayout = React.lazy(() =>
  import("~/layout/_mainSettingsLayout"),
);
const Analytics = React.lazy(() => import("~/containers/analytics/Analytics"));
const Inventory = React.lazy(() =>
  import("~/containers/inventory/InventoryListPage"),
);
const Procedures = React.lazy(() => import("~/pages/Procedures/Procedures"));
const ProcedureView = React.lazy(() =>
  import("~/pages/ProcedureView/ProcedureView"),
);
const ProcedureInstance = React.lazy(() =>
  import("~/pages/ProcedureInstance/ProcedureInstance"),
);
const ProcedureEdit = React.lazy(() =>
  import("~/pages/ProcedureEdit/ProcedureEdit"),
);
const EmailLayoutV2 = React.lazy(() => import("~/pages/Email"));
const AiAssistant = React.lazy(() => import("~/pages/AiAssistant/AiAssistant"));
const AiAssistantChat = React.lazy(() => import("~/pages/AiAssistant/Chat"));
const SingleAssistant = React.lazy(() =>
  import("~/containers/AiAssistant/SingleAssistant"),
);
const AiNotetaker = React.lazy(() => import("~/containers/AiNotetaker"));
const AiNote = React.lazy(() => import("~/containers/AiNote"));
const Scheduler = React.lazy(() => import("~/pages/Scheduler"));
const PlannedEvents = React.lazy(() =>
  import("~/containers/plannedEvents/PlannedEvents"),
);
const QuotesListPage = React.lazy(() =>
  import("~/pages/Quotes/QuotesListPage"),
);
const QuoteDetailPage = React.lazy(() =>
  import("~/pages/Quotes/QuoteDetailPage"),
);
const InvoicesListPage = React.lazy(() =>
  import("~/pages/Invoices/InvoicesListPage"),
);
const InvoiceDetailPage = React.lazy(() =>
  import("~/pages/Invoices/InvoiceDetailPage"),
);

const KnowledgeBase = React.lazy(() => import("~/containers/KnowledgeBase"));
const AISearch = React.lazy(() => import("~/containers/AISearch"));

const routes = [
  {
    id: "app",
    path: "/app/asset-hub",
    exact: true,
    label: "Assets",
    icon: <AssetHubIcon className="icon" />,
    iconActive: <AssetHubIconActive className="icon-active" />,
    iconV2: <AssetHubIcon className="w-3xl h-3xl flex-shrink-0 icon" />,
    iconActiveV2: (
      <AssetHubIconActive className="icon-active h-3xl w-3xl flex-shrink-0" />
    ),
    showInNavigation: true,
    key: "asset-hub",
    Main: () => <Navigate replace to="/app/asset-hub/my-assets" />,
  },
  {
    id: "app",
    path: "/app/work-orders",
    exact: true,
    label: "Tickets",
    enableIf: (user) =>
      user?.oem?.installedProducts?.find(
        (installedProduct) =>
          installedProduct.type === PRODUCT_TYPES.CMMS ||
          installedProduct.type === PRODUCT_TYPES.FIELD_SERVICE,
      ),
    key: "openTickets",
    Main: () => {
      return <Navigate replace to="/app/work/work-orders" />;
    },
  },
  {
    id: "app",
    path: "/app/work/work-orders",
    exact: true,
    label: "Tickets",
    iconV2: <FieldServiceIcon className="icon h-3xl w-3xl flex-shrink-0" />,
    iconActiveV2: (
      <FieldServiceIconActive className="icon-active h-3xl w-3xl flex-shrink-0" />
    ),
    key: "openTickets",
    tooltipKey: "field-service",
    showInNavigation: true,
    Main: ({ user }) => {
      const productType = user?.oem?.installedProducts?.find(
        (installedProduct) =>
          installedProduct.type === PRODUCT_TYPES.CMMS ||
          installedProduct.type === PRODUCT_TYPES.FIELD_SERVICE,
      )?.type;
      return <WorkOrdersPage productType={productType} />;
    },
  },
  {
    id: "app",
    path: "/app/work/quotes",
    exact: true,
    key: "financeQuotes",
    label: "Quotes",
    enableIf: (user, _features, appConfig) =>
      isPaidFeatureAvailable(paidFeatures.quotesAndInvoices, user, appConfig),
    Main: () => <QuotesListPage />,
  },
  {
    id: "app",
    path: "/app/work/quotes/:id",
    exact: true,
    key: "financeQuotes",
    enableIf: (user, _features, appConfig) =>
      isPaidFeatureAvailable(paidFeatures.quotesAndInvoices, user, appConfig),
    Main: () => <QuoteDetailPage />,
  },
  {
    id: "app",
    path: "/app/work/invoices",
    exact: true,
    key: "financeInvoices",
    label: "Invoices",
    enableIf: (user, _features, appConfig) =>
      isPaidFeatureAvailable(paidFeatures.quotesAndInvoices, user, appConfig),
    Main: () => <InvoicesListPage />,
  },
  {
    id: "app",
    path: "/app/work/invoices/:id",
    exact: true,
    key: "financeInvoices",
    enableIf: (user, _features, appConfig) =>
      isPaidFeatureAvailable(paidFeatures.quotesAndInvoices, user, appConfig),
    Main: () => <InvoiceDetailPage />,
  },
  {
    id: "app",
    path: "/app/work/tickets",
    exact: true,
    label: "Tickets",
    iconV2: <FieldServiceIcon className="icon h-3xl w-3xl flex-shrink-0" />,
    iconActiveV2: (
      <FieldServiceIconActive className="icon-active h-3xl w-3xl flex-shrink-0" />
    ),
    key: "supportTickets",
    tooltipKey: "support-tickets",
    showInNavigation: true,
    enableIf: (user) =>
      user?.oem?.installedProducts?.find(
        (installedProduct) =>
          installedProduct.type === PRODUCT_TYPES.CMMS ||
          installedProduct.type === PRODUCT_TYPES.FIELD_SERVICE,
      ),
    Main: () => {
      return <SupportTickets />;
    },
  },
  {
    id: "app",
    path: "/app/work/tickets/:id",
    exact: true,
    label: "Ticket",
    enableIf: (user) =>
      user?.oem?.installedProducts?.find(
        (installedProduct) =>
          installedProduct.type === PRODUCT_TYPES.CMMS ||
          installedProduct.type === PRODUCT_TYPES.FIELD_SERVICE,
      ),
    icon: "far fa-square",
    Main: () => {
      return <SupportTicketViewPage />;
    },
  },
  {
    id: "app",
    path: "/app/work/archived-work-orders",
    exact: true,
    key: "archivedWorkOrders",
    label: "Archived Work Orders",
    enableIf: (user) =>
      user?.oem?.installedProducts?.find(
        (installedProduct) =>
          installedProduct.type === PRODUCT_TYPES.CMMS ||
          installedProduct.type === PRODUCT_TYPES.FIELD_SERVICE,
      ),
    icon: "far fa-square",
    Main: () => {
      return <ArchivedWorkOrdersPage />;
    },
  },
  {
    id: "app",
    path: "/app/work/work-orders/:id",
    exact: true,
    label: "Work Order",
    enableIf: (user) =>
      user?.oem?.installedProducts?.find(
        (installedProduct) =>
          installedProduct.type === PRODUCT_TYPES.CMMS ||
          installedProduct.type === PRODUCT_TYPES.FIELD_SERVICE,
      ),
    icon: "far fa-square",
    Main: () => {
      return <WorkOrderDetailsPage />;
    },
  },
  {
    id: "app",
    path: "/app/work/planned-events",
    exact: true,
    key: "myPlannedEvents",
    label: "My Planned Events",
    icon: <HourglassIcon width={"18"} height={"20"} />,
    iconActive: (
      <HourglassIcon
        width={"18"}
        height={"20"}
        fillColor={COLOR.$blue_v2_100}
      />
    ),
    Main: () => <PlannedEvents />,
  },
  {
    id: "app",
    path: "/app/work/my-work-orders",
    exact: true,
    key: "myTickets",
    label: "My Tickets",
    icon: <SidebarMyTicketfolderIcon className="icon" />,
    iconActive: <SidebarMyTicketFilledIcon className="icon-active" />,
    Main: () => {
      return <MyWorkOrdersPage />;
    },
  },
  {
    id: "app",
    path: "/app/my-work-orders/:id",
    exact: true,
    label: "My Ticket",
    icon: "far fa-square",
    enableIf: (user) =>
      user?.oem?.installedProducts?.find(
        (installedProduct) =>
          installedProduct.type === PRODUCT_TYPES.CMMS ||
          installedProduct.type === PRODUCT_TYPES.FIELD_SERVICE,
      ),
    Main: () => {
      const params = useParams();
      return <Navigate replace to={`/app/work/my-work-orders/${params.id}`} />;
    },
  },
  {
    id: "app",
    path: "/app/work/my-work-orders/:id",
    exact: true,
    label: "My Ticket",
    icon: "far fa-square",
    enableIf: (user) =>
      user?.oem?.installedProducts?.find(
        (installedProduct) =>
          installedProduct.type === PRODUCT_TYPES.CMMS ||
          installedProduct.type === PRODUCT_TYPES.FIELD_SERVICE,
      ),
    Main: () => {
      return <WorkOrderDetailsPage />;
    },
  },
  {
    id: "app",
    path: "/app/assets/machines",
    exact: true,
    label: "Machines",
    key: "machines",
    Main: () => <Navigate replace to="/app/asset-hub/my-assets" />,
  },
  {
    id: "app",
    path: "/app/assets/machines/templates",
    exact: true,
    label: "Machine Templates",
    key: "machines",
    Main: () => <Navigate replace to="/app/asset-hub/my-products" />,
  },
  {
    id: "app",
    path: "/app/assets/templates",
    exact: true,
    label: "Machine Templates",
    key: "machines",
    Main: () => <Navigate replace to="/app/asset-hub/my-products" />,
  },
  {
    id: "app",
    path: "/app/assets",
    exact: true,
    label: "Assets",
    icon: <AssetHubIcon />,
    iconActive: <AssetHubIconActive className="icon-active" />,
    iconV2: <AssetHubIcon className="w-3xl h-3xl flex-shrink-0 icon" />,
    iconActiveV2: (
      <AssetHubIconActive className="icon-active h-3xl w-3xl flex-shrink-0" />
    ),
    iconOld: <AssetsManagement className="icon h-3xl w-3xl flex-shrink-0" />,
    iconActiveOld: (
      <AssetsManagementActive className="icon-active h-3xl w-3xl flex-shrink-0" />
    ),
    showInNavigation: false,
    key: "machines",
    Main: () => <Navigate replace to="/app/asset-hub/my-assets" />,
  },
  {
    id: "app",
    path: "/app/assets/all",
    exact: true,
    label: "Assets",
    key: "machines",
    Main: () => <Navigate replace to="/app/asset-hub" />,
  },
  {
    id: "app",
    path: "/app/assets/production-lines",
    exact: true,
    label: "Production Lines",
    key: "machines",
    Main: () => <Navigate replace to="/app/assets/all" />,
  },
  {
    id: "app",
    path: "/app/assets/components",
    exact: true,
    label: "Components",
    key: "machines",
    Main: () => <Navigate replace to="/app/assets/all" />,
  },
  {
    id: "app",
    path: "/app/assets/machines/templates/:id",
    exact: true,
    label: "Machine Template Details",
    key: "machineTemplateDetails",
    Main: () => {
      const params = useParams();
      return (
        <Navigate replace to={`/app/asset-hub/my-products/${params.id}`} />
      );
    },
  },
  {
    id: "app",
    path: "/app/assets/templates/:id",
    exact: true,
    label: "Machine Template Details",
    key: "machineTemplateDetails",
    Main: () => {
      const params = useParams();
      return (
        <Navigate replace to={`/app/asset-hub/my-products/${params.id}`} />
      );
    },
  },
  {
    id: "app",
    path: "/app/assets/machines/:id",
    exact: true,
    label: "Machines",
    icon: "fas fa-cogs",
    Main: () => {
      const params = useParams();
      return <Navigate replace to={`/app/asset-hub/my-assets/${params.id}`} />;
    },
  },
  {
    id: "app",
    path: "/app/assets/all/:id",
    exact: true,
    label: "Assets",
    icon: "fas fa-cogs",
    Main: () => {
      const params = useParams();
      return <Navigate replace to={`/app/asset-hub/my-assets/${params.id}`} />;
    },
  },
  {
    id: "app",
    path: "/app/facilities",
    exact: true,
    label: "Facilities",
    icon: <SidebarFacilityIcon className="icon" />,
    iconActive: <SidebarFacilityFilledIcon className="icon-active" />,
    iconV2: (
      <SidebarFacilityIconV2 className="icon h-3xl w-3xl flex-shrink-0" />
    ),
    iconActiveV2: (
      <SidebarFacilityFilledIconV2 className="icon-active h-3xl w-3xl flex-shrink-0" />
    ),
    showInNavigation: false,
    key: "facilities",
    Main: () => {
      return <Navigate replace to="/app/asset-hub/connections" />;
    },
  },
  {
    id: "app",
    path: "/app/facilities/:id",
    exact: true,
    label: "Facilities",
    icon: "fas fa-industry",
    Main: () => {
      const params = useParams();
      return (
        <Navigate replace to={`/app/asset-hub/connections/${params.id}`} />
      );
    },
  },
  {
    id: "app",
    path: "/app/settings",
    exact: false,
    Main: (props) => <MainSettingsLayout {...props} />,
  },
  {
    id: "app",
    path: "/app/procedures",
    exact: true,
    label: "Procedures",
    enableIf: (user) =>
      user?.oem?.installedProducts?.find(
        (installedProduct) =>
          installedProduct.type === PRODUCT_TYPES.CMMS ||
          installedProduct.type === PRODUCT_TYPES.FIELD_SERVICE,
      ),
    icon: <SidebarProceduresIcon className="icon" />,
    iconActive: <SidebarProceduresFilledIcon className="icon-active" />,
    key: "procedures",
    Main: () => {
      return <Navigate replace to="/app/work/procedures" />;
    },
  },
  {
    id: "app",
    path: "/app/work/procedures",
    exact: true,
    label: "Procedures",
    icon: <SidebarProceduresIcon className="icon" />,
    iconActive: <SidebarProceduresFilledIcon className="icon-active" />,
    key: "procedures",
    Main: () => {
      return <Procedures />;
    },
  },
  {
    id: "app",
    path: "/app/procedures/:id",
    exact: true,
    label: "Procedures",
    enableIf: (user) =>
      user?.oem?.installedProducts?.find(
        (installedProduct) =>
          installedProduct.type === PRODUCT_TYPES.CMMS ||
          installedProduct.type === PRODUCT_TYPES.FIELD_SERVICE,
      ),
    icon: <SidebarProceduresIcon className="icon" />,
    iconActive: <SidebarProceduresFilledIcon className="icon-active" />,
    key: "procedures",
    Main: () => {
      const params = useParams();
      return <Navigate replace to={`/app/work/procedures/${params.id}`} />;
    },
  },
  {
    id: "app",
    path: "/app/work/procedures/:id",
    exact: true,
    label: "Procedures",
    enableIf: (user) =>
      user?.oem?.installedProducts?.find(
        (installedProduct) =>
          installedProduct.type === PRODUCT_TYPES.CMMS ||
          installedProduct.type === PRODUCT_TYPES.FIELD_SERVICE,
      ),
    icon: <SidebarProceduresIcon className="icon" />,
    iconActive: <SidebarProceduresFilledIcon className="icon-active" />,
    key: "procedures",
    Main: () => {
      return <ProcedureView />;
    },
  },
  {
    id: "app",
    path: "/app/work/procedures/instance/:procedureId",
    exact: true,
    label: "Procedures",
    enableIf: (user) =>
      user?.oem?.installedProducts?.find(
        (installedProduct) =>
          installedProduct.type === PRODUCT_TYPES.CMMS ||
          installedProduct.type === PRODUCT_TYPES.FIELD_SERVICE,
      ),
    icon: <SidebarProceduresIcon className="icon" />,
    iconActive: <SidebarProceduresFilledIcon className="icon-active" />,
    key: "procedures",
    Main: () => {
      return <ProcedureInstance isDetached={true} />;
    },
  },
  {
    id: "app",
    path: "/app/work/procedures/:id/edit",
    exact: true,
    label: "Edit Procedure",
    enableIf: (user) =>
      user?.oem?.installedProducts?.find(
        (installedProduct) =>
          installedProduct.type === PRODUCT_TYPES.CMMS ||
          installedProduct.type === PRODUCT_TYPES.FIELD_SERVICE,
      ),
    icon: <SidebarProceduresIcon className="icon" />,
    iconActive: <SidebarProceduresFilledIcon className="icon-active" />,
    key: "procedures",
    Main: () => {
      return <ProcedureEdit />;
    },
  },
  {
    id: "app",
    path: "/app/work-orders/:ticketId/procedure/:procedureId",
    exact: true,
    label: "Procedures",
    enableIf: (user) =>
      user?.oem?.installedProducts?.find(
        (installedProduct) =>
          installedProduct.type === PRODUCT_TYPES.CMMS ||
          installedProduct.type === PRODUCT_TYPES.FIELD_SERVICE,
      ),
    icon: <SidebarProceduresIcon className="icon" />,
    iconActive: <SidebarProceduresFilledIcon className="icon-active" />,
    key: "procedures",
    Main: () => {
      const params = useParams();
      return (
        <Navigate
          replace
          to={`/app/work/work-orders/${params.ticketId}/procedure/${params.procedureId}`}
        />
      );
    },
  },
  {
    id: "app",
    // The note detail page nested under its work order. `/app/ai/notetaker/:id`
    // lists only the viewer's own notes, so a note recorded by a colleague is
    // reachable only from the work order that carries it.
    path: "/app/work/work-orders/:workOrderId/ai-notes/:noteId",
    exact: true,
    label: "AI Notes",
    enableIf: (user) =>
      user?.oem?.installedProducts?.find(
        (installedProduct) =>
          installedProduct.type === PRODUCT_TYPES.CMMS ||
          installedProduct.type === PRODUCT_TYPES.FIELD_SERVICE,
      ),
    key: "ai-notetaker",
    Main: () => {
      return <AiNote />;
    },
  },
  {
    id: "app",
    path: "/app/work/work-orders/:ticketId/procedure/:procedureId",
    exact: true,
    label: "Procedures",
    enableIf: (user) =>
      user?.oem?.installedProducts?.find(
        (installedProduct) =>
          installedProduct.type === PRODUCT_TYPES.CMMS ||
          installedProduct.type === PRODUCT_TYPES.FIELD_SERVICE,
      ),
    icon: <SidebarProceduresIcon className="icon" />,
    iconActive: <SidebarProceduresFilledIcon className="icon-active" />,
    key: "procedures",
    Main: () => {
      return <ProcedureInstance />;
    },
  },
  {
    id: "app",
    path: "/app/inventory",
    exact: true,
    label: "Inventory",
    key: "inventory",
    icon: <InventoryIcon className="icon" />,
    iconActive: <InventoryFilledIcon className="icon-active" />,
    Main: () => <Navigate replace to="/app/assets/inventory" />,
  },
  {
    id: "app",
    path: "/app/assets/inventory",
    exact: true,
    label: "Inventory",
    icon: <InventoryIcon className="icon" />,
    iconActive: <InventoryFilledIcon className="icon-active" />,
    key: "inventory",
    Main: () => {
      return <Navigate replace to="/app/asset-hub/inventory" />;
    },
  },
  {
    id: "app",
    path: "/app/assets/production-lines/:id",
    exact: true,
    key: "productionLineDetails",
    Main: () => {
      const params = useParams();
      return <Navigate replace to={`/app/assets/all/${params.id}`} />;
    },
  },
  {
    id: "app",
    path: "/app/assets/components/:id",
    exact: true,
    key: "componentDetails",
    Main: () => {
      const params = useParams();
      return <Navigate replace to={`/app/assets/all/${params.id}`} />;
    },
  },
  {
    id: "app",
    path: "/app/emails/:id",
    exact: true,
    label: "Emails",
    enableIf: (user) =>
      user?.oem?.installedProducts?.find(
        (installedProduct) =>
          installedProduct.type === PRODUCT_TYPES.CMMS ||
          installedProduct.type === PRODUCT_TYPES.FIELD_SERVICE,
      ),
    icon: <EnvelopOutline className="icon" />,
    iconActive: <EnvelopFilled className="icon-active" />,
    key: "emails",
    Main: () => {
      const params = useParams();
      return <Navigate replace to={`/app/work/emails/${params.id}`} />;
    },
  },
  {
    id: "app",
    path: "/app/work/emails",
    exact: false,
    label: "Emails",
    icon: <EnvelopOutline className="icon" />,
    iconActive: <EnvelopFilled className="icon-active" />,
    key: "emails",
    // Disabled entirely when the OEM's email feature is turned off (CRM).
    enableIf: (user, _appFeatures, appConfig) =>
      isPaidFeatureAvailable(paidFeatures.emails, user, appConfig),
    Main: EmailLayoutV2,
  },
  {
    id: "app",
    path: "/app/ai",
    exact: true,
    label: "Industrial AI",
    iconOld: <AiIcon className="icon h-3xl w-3xl flex-shrink-0" />,
    iconActiveOld: (
      <AiIconFilled className="icon-active h-3xl w-3xl flex-shrink-0" />
    ),
    iconV2: <IndustrialAiIcon className="icon w-3xl h-3xl flex-shrink-0" />,
    iconActiveV2: (
      <IndustrialAiIconActive className="icon-active w-3xl h-3xl flex-shrink-0" />
    ),
    showInNavigation: true,
    key: "industrial-ai",
    Main: ({ user, appConfig }) => (
      <Navigate
        replace
        to={
          !isPaidFeatureAvailable(paidFeatures.aiAssistants, user, appConfig) &&
          isPaidFeatureAvailable(paidFeatures.aiNotetaker, user, appConfig)
            ? "/app/ai/notetaker"
            : "/app/ai/assistant"
        }
      />
    ),
  },
  {
    exact: true,
    key: "ai-assistant",
    Main: AiAssistant,
    path: "/app/ai/assistant",
  },
  {
    exact: true,
    key: "ai-assistant",
    Main: SingleAssistant,
    path: "/app/ai/assistant/:id",
  },
  {
    exact: true,
    key: "ai-chats",
    Main: AiAssistantChat,
    path: "/app/ai/chat",
  },
  {
    exact: true,
    key: "ai-chats",
    Main: AiAssistantChat,
    path: "/app/ai/chat/:id",
  },
  {
    exact: true,
    key: "ai-assistant",
    Main: () => <Navigate replace to="/app/ai/assistant" />,
    path: "/app/ai-assistant",
  },
  {
    exact: true,
    key: "ai-notetaker",
    Main: AiNotetaker,
    path: "/app/ai/notetaker",
  },
  {
    enableIf: (user) =>
      user?.oem?.installedProducts?.find(
        (installedProduct) =>
          installedProduct.type === PRODUCT_TYPES.INDUSTRIAL_AI,
      ),
    exact: true,
    key: "knowledge-base",
    Main: KnowledgeBase,
    path: "/app/ai/knowledge-base",
  },
  {
    enableIf: (user) =>
      user?.oem?.installedProducts?.find(
        (installedProduct) =>
          installedProduct.type === PRODUCT_TYPES.INDUSTRIAL_AI,
      ),
    exact: true,
    key: "ai-search",
    Main: () => <AISearch />,
    path: "/app/ai/search",
  },
  {
    exact: true,
    key: "ai-notetaker",
    Main: AiNote,
    path: "/app/ai/notetaker/:id",
  },
  {
    id: "app",
    path: "/app/work/insights",
    showInNavigation: false,
    label: "Insights",
    key: "analytics",
    icon: <SidebarAnalyticsIcon className="icon" />,
    iconActive: <SidebarAnalyticsFilledIcon className="icon-active" />,
    iconV2: (
      <SidebarAnalyticsIconV2 className="icon w-3xl h-3xl flex-shrink-0" />
    ),
    iconActiveV2: (
      <SidebarAnalyticsFilledIconV2 className="icon-active w-3xl h-3xl flex-shrink-0" />
    ),
    Main: () => <Analytics />,
  },
  // Redirects from old "analytics" URLs to new "insights" URLs
  {
    id: "app",
    path: "/app/work/analytics",
    exact: true,
    Main: () => <Navigate replace to="/app/work/insights" />,
  },
  {
    id: "app",
    path: "/app/work/analytics/new-report/:id",
    exact: true,
    Main: () => {
      const { id } = useParams();
      return <Navigate replace to={`/app/work/insights/new-report/${id}`} />;
    },
  },
  {
    id: "app",
    path: "/app/work/analytics/:id",
    exact: true,
    Main: () => {
      const { id } = useParams();
      return <Navigate replace to={`/app/work/insights/${id}`} />;
    },
  },
  {
    exact: true,
    icon: <CalendarBlank className="icon w-5 h-5 text-secondary" />,
    iconActive: <CalendarBlankFilled className="icon-active w-5 h-5" />,
    key: "scheduler",
    Main: () => <Scheduler />,
    path: "/app/work/scheduler",
  },
  {
    exact: true,
    enableIf: (user) =>
      user?.oem?.installedProducts?.find(
        (installedProduct) =>
          installedProduct.type === PRODUCT_TYPES.CMMS ||
          installedProduct.type === PRODUCT_TYPES.FIELD_SERVICE,
      ),
    icon: <CalendarBlank className="icon w-5 h-5 text-secondary" />,
    iconActive: <CalendarBlankFilled className="icon-active w-5 h-5" />,
    key: "scheduler",
    Main: () => {
      return <Navigate replace to="/app/work/scheduler" />;
    },
    path: "/app/scheduler",
  },
  {
    id: "app",
    path: "/app/asset-hub/my-assets",
    exact: true,
    label: "My Assets",
    key: "asset-hub",
    Main: () => <Assets />,
  },
  {
    id: "app",
    path: "/app/asset-hub/my-assets/:id",
    exact: true,
    label: "Assets",
    icon: "fas fa-cogs",
    Main: () => <AssetView />,
  },
  {
    id: "app",
    path: "/app/asset-hub/shared-assets",
    exact: true,
    label: "Shared Assets",
    key: "asset-hub",
    Main: () => <Navigate replace to="/app/asset-hub/shared" />,
  },
  {
    id: "app",
    path: "/app/asset-hub/shared-assets/:id",
    exact: true,
    label: "Shared Assets",
    key: "asset-hub",
    Main: () => {
      const params = useParams();
      return <Navigate replace to={`/app/asset-hub/shared/${params.id}`} />;
    },
  },
  {
    id: "app",
    path: "/app/workspace/:id",
    label: "Future Vision",
    isWorkspace: true,
    hideNavigation: true,
    Main: () => (
      <WorkspaceLayout>
        <CustomerPortalLayout />
      </WorkspaceLayout>
    ),
  },
  {
    id: "app",
    path: "/app/asset-hub/shared/:id",
    exact: true,
    label: "Shared Assets",
    key: "asset-hub",
    Main: () => {
      const params = useParams();
      return <Navigate replace to={`/app/workspace/${params.id}`} />;
    },
  },
  {
    id: "app",
    path: "/app/asset-hub/shared/:connectionId/assets/:assetId",
    exact: true,
    label: "Shared Asset",
    icon: "fas fa-industry",
    Main: () => {
      const params = useParams();
      return (
        <Navigate
          replace
          to={`/app/workspace/${params.connectionId}/assets/${params.assetId}`}
        />
      );
    },
  },
  {
    id: "app",
    path: "/app/asset-hub/shared/:connectionId/products/:productId",
    exact: true,
    label: "Shared Asset",
    icon: "fas fa-industry",
    Main: () => {
      const params = useParams();
      return (
        <Navigate
          replace
          to={`/app/workspace/${params.connectionId}/products/${params.productId}`}
        />
      );
    },
  },
  {
    id: "app",
    path: "/app/asset-hub/shared-assets/:connectionId/:assetId",
    exact: true,
    label: "Shared Asset",
    icon: "fas fa-industry",
    Main: () => {
      const params = useParams();
      return (
        <Navigate
          replace
          to={`/app/asset-hub/shared/${params.connectionId}/assets/${params.assetId}`}
        />
      );
    },
  },
  {
    id: "app",
    path: "/app/asset-hub/connections",
    exact: true,
    label: "Connections",
    key: "asset-hub",
    // The legacy path lands on whichever spelling the account uses, so links
    // that still point at /connections follow the account's nomenclature.
    Main: () => {
      const { basePath } = useNomenclature();
      return <Navigate replace to={basePath} />;
    },
  },
  {
    id: "app",
    path: "/app/asset-hub/companies",
    exact: true,
    label: "Connections",
    key: "asset-hub",
    Main: () => <Connections />,
  },
  {
    // CMMS accounts browse the module here. Both paths stay registered so
    // existing links and bookmarks keep resolving either way. No `key`: it is
    // the React key in _main.jsx and would collide with the /companies route.
    id: "app",
    path: "/app/asset-hub/locations",
    exact: true,
    label: "Connections",
    Main: () => <Connections />,
  },
  {
    id: "app",
    path: "/app/asset-hub/connections/:id",
    exact: true,
    label: "Connections",
    icon: "fas fa-industry",
    Main: () => {
      const params = useParams();
      const { pathTo } = useNomenclature();
      return <Navigate replace to={pathTo(params.id)} />;
    },
  },
  {
    id: "app",
    path: "/app/asset-hub/companies/:id",
    exact: true,
    label: "Companies",
    icon: "fas fa-industry",
    Main: () => {
      return <SingleConnection />;
    },
  },
  {
    id: "app",
    path: "/app/asset-hub/locations/:id",
    exact: true,
    label: "Companies",
    icon: "fas fa-industry",
    Main: () => {
      return <SingleConnection />;
    },
  },
  {
    id: "app",
    path: "/app/asset-hub/contacts",
    exact: true,
    label: "Contacts",
    key: "asset-hub",
    Main: () => <AllContacts />,
  },
  {
    id: "app",
    path: "/app/asset-hub/connection-requests",
    exact: true,
    label: "ConnectionRequests",
    icon: "fas fa-industry",
    Main: () => {
      return <ConnectionRequests />;
    },
  },
  {
    id: "app",
    path: "/app/asset-hub/inventory",
    exact: true,
    label: "Connections",
    key: "asset-hub",
    Main: () => <Inventory />,
  },
  {
    id: "app",
    path: "/app/customer-portal/my-portals",
    exact: true,
    label: "Customer Portal",
    iconV2: <CustomerPortal className="icon h-3xl w-3xl flex-shrink-0" />,
    iconActiveV2: (
      <CustomerPortalActive className="icon-active h-3xl w-3xl flex-shrink-0" />
    ),
    tooltipKey: "customerPortal",
    showInNavigation: true,
    Main: () => <MyPortalsFutureVision />,
  },
  {
    id: "app",
    path: "/app/customer-portal/my-portals/:id",
    exact: true,
    label: "Customer Portal",
    enableIf: (user, features, appConfig) =>
      isPaidFeatureAvailable(paidFeatures.customerPortal, user, appConfig) &&
      features.includes(appFeatures.productCatalog),
    Main: () => <SinglePortal />,
  },
  {
    id: "app",
    path: "/app/customer-portal/shared-products",
    exact: true,
    enableIf: (user, _features, appConfig) =>
      isPaidFeatureAvailable(paidFeatures.customerPortal, user, appConfig),
    label: "Shared Products",
    key: "customerPortal",
    Main: () => <SharedProducts />,
  },
  {
    id: "app",
    path: "/app/asset-hub/asset-templates",
    exact: true,
    label: "Asset Templates",
    key: "asset-hub",
    Main: () => {
      const isProductCatalogEnabled = useIsProductCatalogEnabled();
      return isProductCatalogEnabled ? (
        <Navigate replace to="/app/asset-hub/my-products" />
      ) : (
        <AssetTemplates />
      );
    },
  },
  {
    id: "app",
    path: "/app/asset-hub/my-products",
    exact: true,
    label: "My Products",
    key: "asset-hub",
    Main: () => <AssetTemplates />,
  },
  {
    id: "app",
    path: "/app/asset-hub/asset-templates/:id",
    exact: true,
    label: "Asset Template Details",
    key: "asset-hub",
    Main: () => {
      const params = useParams();
      return (
        <Navigate replace to={`/app/asset-hub/my-products/${params.id}`} />
      );
    },
  },
  {
    id: "app",
    path: "/app/asset-hub/my-products/:id",
    exact: true,
    label: "Product Details",
    key: "asset-hub",
    Main: () => <AssetTemplateSingle />,
  },
];

export default (user, appFeatures, appConfig) =>
  routes.filter(({ enableIf = () => true }) =>
    enableIf(user, appFeatures, appConfig),
  );
